import { AlertCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { uk } from '../i18n/uk';
import { cx } from '../lib/cx';
import { Button } from './Button';

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('animate-pulse rounded-lg bg-hint/15', className)} />;
}

/** Placeholder list rows while data loads. */
export function ListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="mx-4 overflow-hidden rounded-xl bg-section" aria-busy="true">
      <span className="sr-only">{uk.common.loading}</span>
      {Array.from({ length: rows }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder rows
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          <Skeleton className="size-14 shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/5" />
            <Skeleton className="h-3.5 w-2/5" />
          </div>
        </div>
      ))}
    </div>
  );
}

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  text?: string;
  action?: ReactNode;
}

export function EmptyState({ icon, title, text, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center px-8 py-12 text-center">
      {icon && <div className="mb-4 text-hint">{icon}</div>}
      <h2 className="text-[20px] font-semibold">{title}</h2>
      {text && <p className="mt-2 text-[15px] leading-snug text-subtitle">{text}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  return (
    <EmptyState
      icon={<AlertCircle className="size-10" />}
      title={uk.common.error}
      {...(message ? { text: message } : {})}
      action={onRetry && <Button onClick={onRetry}>{uk.common.retry}</Button>}
    />
  );
}
